import * as THREE from "three";
import { buildLevel, tileKey, type LevelData } from "./level";
import { buildLevel2 } from "./level2";
import { buildLevel3 } from "./level3";
import { buildLevel4 } from "./level4";
import { buildLevel2_1 } from "./level2_1";
import { buildLevel2_2 } from "./level2_2";
import { buildLevel2_3 } from "./level2_3";
import { buildLevel2_4 } from "./level2_4";
import { buildLevel3_1 } from "./level3_1";
import { buildLevel3_2 } from "./level3_2";
import { buildLevel3_3 } from "./level3_3";
import { buildLevel3_4 } from "./level3_4";
import { buildLevel4_1 } from "./level4_1";
import { buildLevel4_2 } from "./level4_2";
import { buildLevel4_3 } from "./level4_3";
import { buildLevel4_4 } from "./level4_4";
import { hasGroundAhead, isSolidAt, moveBody, type Body } from "./physics";
import { Sfx } from "./sfx";
import {
  ENEMY_SPEED,
  GRAVITY,
  JUMP_CUT_MULT,
  JUMP_VELOCITY,
  MAX_FALL,
  MUSHROOM_SPEED,
  PLAYER_ACCEL,
  PLAYER_FRICTION,
  PLAYER_RUN_SPEED,
  PLAYER_WALK_SPEED,
  SHELL_KICK_SPEED,
  START_TIME,
  type CoinPop,
  type EnemyState,
  type GamePhase,
  type GameStats,
  type InputState,
  type PowerupState,
  type TileData
} from "./types";
import {
  makeCastle,
  makeFlag,
  makeFlower,
  makeGoomba,
  makeKoopa,
  makeMario,
  makeMushroom,
  makeParatroopa,
  tileColor,
  type Animatable,
  type FlagRig
} from "./world";

interface EnemyEnt extends EnemyState {
  rig: Animatable;
}

interface PowerupEnt extends PowerupState {
  rig: Animatable;
  exitTo: number;
}

interface CoinPopEnt extends CoinPop {
  mesh: THREE.Mesh;
}

interface BumpEnt {
  key: string;
  mesh: THREE.Mesh;
  baseY: number;
  timer: number;
}

export interface HudInfo extends GameStats {
  phase: GamePhase;
  message: string;
  world: string;
}

const SMALL_H = 0.95;
const BIG_H = 1.7;
const SMALL_W = 0.68;

function aabb(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number
): boolean {
  return (
    Math.abs(ax - bx) * 2 < aw + bw &&
    Math.abs(ay - by) * 2 < ah + bh
  );
}

export class Game {
  private sfx: Sfx;
  private camera: THREE.PerspectiveCamera;

  private world = 1;
  private levelNum = 1;
  private level: LevelData = buildLevel();
  private worldGroup = new THREE.Group();
  private tileMeshes = new Map<string, THREE.Mesh>();
  private bumps: BumpEnt[] = [];

  private mario = makeMario();
  private player = {
    x: 2,
    y: SMALL_H / 2,
    vx: 0,
    vy: 0,
    w: SMALL_W,
    h: SMALL_H,
    onGround: false,
    facing: 1 as 1 | -1,
    power: "small" as "small" | "big" | "fire",
    crouching: false,
    invulnTimer: 0,
    alive: true,
    deathTimer: 0,
    walkPhase: 0
  };

  private enemies: EnemyEnt[] = [];
  private powerups: PowerupEnt[] = [];
  private coinPops: CoinPopEnt[] = [];
  private flagRig: FlagRig | null = null;
  private castleMesh: THREE.Group | null = null;

  private stats: GameStats = { score: 0, coins: 0, lives: 3, time: START_TIME };
  private playerPower: "small" | "big" | "fire" = "small";
  private phase: GamePhase = "playing";
  private message = "";
  private msgTimer = 0;
  private timeAcc = 0;
  private phaseTimer = 0;
  private winFlagY = 16;
  private winStartY = 0;

  // pipe enter state
  private pipeEnter: {
    entering: boolean;
    entryPx: number;
    exitX: number;
    exitY: number;
    timer: number;
  } = { entering: false, entryPx: 0, exitX: 0, exitY: 0, timer: 0 };

  onHud: ((info: HudInfo) => void) | null = null;

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, sfx: Sfx) {
    this.camera = camera;
    this.sfx = sfx;
    scene.add(this.worldGroup);
    this.mario.setPower("small");
    scene.add(this.mario.group);
    this.buildWorld();
    this.spawnEnemies();
  }

  private buildWorld(): void {
    this.worldGroup.clear();
    this.tileMeshes.clear();
    this.bumps = [];

    for (const [key, tile] of this.level.tiles) {
      const [xs, ys] = key.split(",");
      const x = Number(xs);
      const y = Number(ys);
      if (tile.type === 7) continue; // pole drawn separately
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshLambertMaterial({ color: tileColor(tile.type) })
      );
      mesh.position.set(x + 0.5, y + 0.5, 0);
      this.worldGroup.add(mesh);
      this.tileMeshes.set(key, mesh);
    }

    // flowers on pipes
    for (const [key, tile] of this.level.tiles) {
      if (tile.type === 5 && tile.flower) {
        const [xs] = key.split(",");
        const x = Number(xs);
        const flower = makeFlower();
        flower.position.set(x + 0.5, 0.5, 0);
        this.worldGroup.add(flower);
      }
    }

    this.flagRig = makeFlag(this.level.flagX, 0);
    this.worldGroup.add(this.flagRig.group);

    this.castleMesh = makeCastle();
    this.castleMesh.position.set(this.level.castleX + 3.5, 0, 0);
    this.worldGroup.add(this.castleMesh);

    // scenery
    const cloudMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    for (let cx = 10; cx < this.level.width; cx += 34) {
      for (let i = 0; i < 3; i++) {
        const c = new THREE.Mesh(new THREE.BoxGeometry(1.8 - i * 0.3, 0.8, 0.8), cloudMat);
        c.position.set(cx + i * 1.1, 14 + (i % 2), -7);
        this.worldGroup.add(c);
      }
    }
    const hillMat = new THREE.MeshLambertMaterial({ color: 0x30a030 });
    for (let hx = 6; hx < this.level.width; hx += 60) {
      const hill = new THREE.Mesh(new THREE.BoxGeometry(10, 4, 1), hillMat);
      hill.position.set(hx, 2, -6);
      this.worldGroup.add(hill);
    }
  }

  private spawnEnemies(): void {
    for (const e of this.enemies) {
      this.worldGroup.remove(e.rig.group);
    }
    this.enemies = [];
    for (const spawn of this.level.enemies) {
      const isKoopa = spawn.kind === "koopa";
      const isParatroopa = spawn.kind === "paratroopa";
      const rig = isParatroopa ? makeParatroopa() : isKoopa ? makeKoopa(false) : makeGoomba();
      const ent: EnemyEnt = {
        kind: spawn.kind,
        x: spawn.x + 0.5,
        y: isKoopa || isParatroopa ? 0.55 : 0.4,
        vx: 0,
        vy: 0,
        w: (isKoopa || isParatroopa) ? 0.7 : 0.78,
        h: (isKoopa || isParatroopa) ? 1.1 : 0.8,
        active: false,
        dead: false,
        squishTimer: 0,
        facing: -1,
        walkPhase: Math.random() * 10,
        onGround: false,
        flapTimer: isParatroopa ? 2 + Math.random() * 2 : undefined,
        rig
      };
      rig.group.position.set(ent.x, 0, 0);
      this.worldGroup.add(rig.group);
      this.enemies.push(ent);
    }
  }

  resetLevel(): void {
    this.level = this.getLevelData(this.world, this.levelNum);
    this.buildWorld();
    this.spawnEnemies();
    this.powerups = [];
    this.coinPops = [];
    this.pipeEnter = { entering: false, entryPx: 0, exitX: 0, exitY: 0, timer: 0 };
    this.resetPlayer(false);
    this.stats.time = START_TIME;
    this.timeAcc = 0;
    this.phase = "playing";
    this.message = this.world > 1 || this.levelNum > 1 ? `WORLD ${this.world}-${this.levelNum}` : "";
    this.msgTimer = this.world > 1 || this.levelNum > 1 ? 2.5 : 0;
    this.phaseTimer = 0;
    this.pushHud();
  }

  private getLevelData(world: number, num: number): LevelData {
    const key = `${world}-${num}`;
    switch (key) {
      case "1-1": return buildLevel();
      case "1-2": return buildLevel2();
      case "1-3": return buildLevel3();
      case "1-4": return buildLevel4();
      case "2-1": return buildLevel2_1();
      case "2-2": return buildLevel2_2();
      case "2-3": return buildLevel2_3();
      case "2-4": return buildLevel2_4();
      case "3-1": return buildLevel3_1();
      case "3-2": return buildLevel3_2();
      case "3-3": return buildLevel3_3();
      case "3-4": return buildLevel3_4();
      case "4-1": return buildLevel4_1();
      case "4-2": return buildLevel4_2();
      case "4-3": return buildLevel4_3();
      case "4-4": return buildLevel4_4();
      default: return buildLevel();
    }
  }

  private resetPlayer(fullReset = false): void {
    const p = this.player;
    p.power = fullReset ? "small" : this.playerPower;
    p.h = p.power === "small" ? SMALL_H : BIG_H;
    p.w = SMALL_W;
    // 1-2 starts on an elevated platform (top at y=5)
    const groundTop = (this.world === 1 && this.levelNum === 2) ? 5 : 0; // 1-2 starts elevated
    p.x = 2;
    p.y = groundTop + p.h / 2;
    p.vx = 0;
    p.vy = 0;
    p.facing = 1;
    p.invulnTimer = 0;
    p.alive = true;
    p.deathTimer = 0;
    p.crouching = false;
    p.onGround = false;
    this.mario.setPower(p.power);
    this.mario.group.visible = true;
  }

  private pushHud(): void {
    this.onHud?.({
      score: this.stats.score,
      coins: this.stats.coins,
      lives: this.stats.lives,
      time: Math.max(0, Math.ceil(this.stats.time)),
      phase: this.phase,
      message: this.message,
      world: `${this.world}-${this.levelNum}`
    });
  }

  private addScore(n: number): void {
    this.stats.score += n;
  }

  private setPower(power: "small" | "big" | "fire"): void {
    const p = this.player;
    const oldH = p.h;
    p.power = power;
    this.playerPower = power;
    p.h = power === "small" ? SMALL_H : BIG_H;
    p.y += (p.h - oldH) / 2;
    this.mario.setPower(power);
  }

  private killEnemy(e: EnemyEnt, byShell: boolean): void {
    const isKoopaLike = e.kind === "koopa" || e.kind === "paratroopa";
    if (isKoopaLike) {
      e.kind = "shell";
      e.h = 0.6;
      e.y = 0.3;
      e.vx = 0;
      e.flapTimer = undefined;
      this.worldGroup.remove(e.rig.group);
      const rig = makeKoopa(true);
      rig.group.position.set(e.x, 0, 0);
      this.worldGroup.add(rig.group);
      e.rig = rig;
    } else {
      e.dead = true;
      e.squishTimer = 0.5;
      e.vx = 0;
      e.rig.group.scale.y = 0.25;
    }
    this.addScore(byShell ? 100 : isKoopaLike ? 200 : 100);
  }

  private kickShell(e: EnemyEnt, dir: 1 | -1): void {
    e.kind = "shell";
    if (e.h > 0.7) {
      e.h = 0.6;
      e.y = 0.3;
      this.worldGroup.remove(e.rig.group);
      const rig = makeKoopa(true);
      rig.group.position.set(e.x, 0, 0);
      this.worldGroup.add(rig.group);
      e.rig = rig;
    }
    e.vx = SHELL_KICK_SPEED * dir;
    e.facing = dir;
    this.sfx.kick();
    this.addScore(400);
  }

  private damagePlayer(): void {
    const p = this.player;
    if (p.invulnTimer > 0 || !p.alive) return;
    if (p.power === "small") {
      this.startDeath();
    } else {
      this.setPower("small");
      p.invulnTimer = 2;
      this.sfx.powerdown();
    }
  }

  private startDeath(): void {
    const p = this.player;
    if (!p.alive) return;
    p.alive = false;
    p.deathTimer = 0;
    p.vy = 13;
    p.vx = 0;
    this.phase = "dying";
    this.phaseTimer = 0;
    this.sfx.die();
    this.pushHud();
  }

  private startWin(): void {
    this.phase = "won";
    this.phaseTimer = 0;
    this.winFlagY = 16;
    this.winStartY = this.player.y;
    this.addScore(4000 + Math.floor(this.stats.time) * 10);
    this.sfx.win();
    this.player.vx = 0;
    this.pushHud();
  }

  private triggerBlockHit(tx: number, ty: number): void {
    const key = tileKey(tx, ty);
    const tile = this.level.tiles.get(key);
    if (!tile) return;

    if (tile.type === 3) {
      // question block
      tile.type = 4;
      this.bumps.push({ key, mesh: this.tileMeshes.get(key)!, baseY: ty + 0.5, timer: 0.25 });
      this.updateTileMesh(key, tile);
      if (tile.payload?.kind === "coin") {
        this.spawnCoinPop(tx, ty);
        this.stats.coins++;
        this.addScore(200);
        if (this.stats.coins >= 100) {
          this.stats.coins -= 100;
          this.stats.lives++;
        }
        this.sfx.coin();
      } else if (tile.payload?.kind === "mushroom") {
        const rig = makeMushroom();
        rig.group.position.set(tx + 0.5, ty + 1.1, 0);
        this.worldGroup.add(rig.group);
        this.powerups.push({
          x: tx + 0.5,
          y: ty + 1.1,
          vx: 0,
          vy: 0,
          w: 0.7,
          h: 0.8,
          exiting: true,
          dead: false,
          rig,
          exitTo: ty + 1.8
        });
        this.sfx.powerup();
      }
      this.pushHud();
    } else if (tile.type === 2) {
      // brick
      if (this.player.power === "small") {
        this.bumps.push({ key, mesh: this.tileMeshes.get(key)!, baseY: ty + 0.5, timer: 0.25 });
        this.sfx.bump();
      } else {
        this.breakBrick(key);
      }
    } else {
      this.sfx.bump();
      const mesh = this.tileMeshes.get(key);
      if (mesh) this.bumps.push({ key, mesh, baseY: ty + 0.5, timer: 0.2 });
    }
  }

  private breakBrick(key: string): void {
    this.level.tiles.delete(key);
    const mesh = this.tileMeshes.get(key);
    if (mesh) {
      this.worldGroup.remove(mesh);
      this.tileMeshes.delete(key);
    }
    this.addScore(50);
    this.sfx.brk();
    this.pushHud();
  }

  private updateTileMesh(key: string, tile: TileData): void {
    const mesh = this.tileMeshes.get(key);
    if (mesh) {
      (mesh.material as THREE.MeshLambertMaterial).color.setHex(tileColor(tile.type));
    }
  }

  private spawnCoinPop(tx: number, ty: number): void {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.5, 0.1),
      new THREE.MeshLambertMaterial({ color: 0xf8d020 })
    );
    mesh.position.set(tx + 0.5, ty + 1.2, 0);
    this.worldGroup.add(mesh);
    this.coinPops.push({ x: tx + 0.5, y: ty + 1.2, vy: 9, life: 0.7, mesh });
  }

  private updatePlayer(dt: number, input: InputState): void {
    const p = this.player;

    if (!p.alive) {
      // death animation
      p.deathTimer += dt;
      p.vy += GRAVITY * dt;
      p.y += p.vy * dt;
      this.mario.group.position.set(p.x, p.y - p.h / 2, 0);
      this.mario.update(0, false);
      return;
    }

    if (p.invulnTimer > 0) p.invulnTimer -= dt;
    this.mario.group.visible = p.invulnTimer > 0 ? Math.floor(p.invulnTimer * 12) % 2 === 0 : true;

    p.crouching = input.down && p.onGround;
    this.mario.setCrouch(p.crouching);

    const targetSpeed = (input.run ? PLAYER_RUN_SPEED : PLAYER_WALK_SPEED) * (input.left ? -1 : input.right ? 1 : 0);
    if (targetSpeed !== 0) {
      p.facing = targetSpeed > 0 ? 1 : -1;
      if (Math.sign(p.vx) !== Math.sign(targetSpeed) || Math.abs(p.vx) < Math.abs(targetSpeed)) {
        p.vx = THREE.MathUtils.clamp(p.vx + Math.sign(targetSpeed) * PLAYER_ACCEL * dt, -Math.abs(targetSpeed), Math.abs(targetSpeed));
      }
    } else {
      const f = PLAYER_FRICTION * dt;
      if (Math.abs(p.vx) <= f) p.vx = 0;
      else p.vx -= Math.sign(p.vx) * f;
    }

    if (input.jumpPressed && p.onGround && !p.crouching) {
      p.vy = JUMP_VELOCITY;
      p.onGround = false;
      this.sfx.jump();
    }
    if (!input.jump && p.vy > JUMP_VELOCITY * JUMP_CUT_MULT) {
      p.vy = JUMP_VELOCITY * JUMP_CUT_MULT;
    }

    p.vy = Math.max(MAX_FALL, p.vy + GRAVITY * dt);

    const prevVy = p.vy;
    const body: Body = { x: p.x, y: p.y, w: p.w, h: p.h, vx: p.vx, vy: p.vy };
    const res = moveBody(this.level, body, dt);
    p.x = body.x;
    p.y = body.y;
    p.vx = body.vx;
    p.vy = body.vy;
    p.onGround = res.onGround;

    if (res.hitCeiling && prevVy > 3) {
      this.triggerBlockHit(res.ceilingTx, res.ceilingTy);
    }

    // walking anim
    if (Math.abs(p.vx) > 0.3 && p.onGround) p.walkPhase += dt * Math.abs(p.vx) * 1.6;

    // fell into pit
    if (p.y < -5) this.startDeath();

    // flag
    if (this.phase === "playing" && p.x + p.w / 2 >= this.level.flagX) {
      this.startWin();
    }

    // pipe enter / exit
    if (this.pipeEnter.entering) {
      this.pipeEnter.timer += dt;
      if (this.pipeEnter.timer < 0.5) {
        // shrinking animation: slide down the pipe
        const entryPy = this.pipeEnter.entryPx;
        p.x = entryPy + 0.5;
        p.y -= dt * 6; // slide down
        p.vx = 0;
        p.vy = 0;
        p.onGround = false;
        // hide mario
        this.mario.group.visible = false;
      } else if (this.pipeEnter.timer < 1.2) {
        // cutscene: player is in the pipe
        this.mario.group.visible = false;
      } else {
        // exit: move to exit pipe and show mario
        const exit = this.pipeEnter;
        p.x = exit.exitX + 0.5;
        p.y = exit.exitY + p.h / 2;
        p.vx = 0;
        p.vy = 0;
        p.onGround = false;
        this.mario.group.visible = true;
        this.pipeEnter = { entering: false, entryPx: 0, exitX: 0, exitY: 0, timer: 0 };
      }
      this.mario.group.position.set(p.x, p.y - p.h / 2, 0);
      this.mario.update(p.walkPhase, false);
      return;
    }

    // check pipe enter: standing on an enterable pipe, pressing down
    if (input.down && p.onGround && p.power === "small") {
      const playerTileX = Math.floor(p.x);
      const playerTileX2 = Math.floor(p.x + p.w / 2);
      const pipeEntry = this.level.enterablePipes.find(ep =>
        (ep.px === playerTileX || ep.px === playerTileX2) &&
        this.level.tiles.has(tileKey(ep.px, 0)) &&
        this.level.tiles.has(tileKey(ep.px + 1, 0))
      );
      if (pipeEntry) {
        this.pipeEnter = { entering: true, entryPx: pipeEntry.px, exitX: pipeEntry.exitX, exitY: pipeEntry.exitY, timer: 0 };
        this.sfx.pipe();
        return;
      }
    }

    this.mario.group.position.set(p.x, p.y - p.h / 2, 0);
    this.mario.group.rotation.y = 0;
    this.mario.group.scale.x = p.facing;
    this.mario.update(p.walkPhase, Math.abs(p.vx) > 0.3);
  }

  private updateEnemies(dt: number, input: InputState): void {
    const p = this.player;
    const toRemove: EnemyEnt[] = [];

    for (const e of this.enemies) {
      if (e.dead) {
        e.squishTimer -= dt;
        if (e.squishTimer <= 0) toRemove.push(e);
        continue;
      }

      if (!e.active) {
        if (p.x > e.x - 20) {
          e.active = true;
          e.rig.group.position.set(e.x, e.y - e.h / 2, 0);
        } else {
          continue;
        }
      }

      e.vy = Math.max(MAX_FALL, e.vy + GRAVITY * dt);

      // paratroopa flapping: periodically jump
      if (e.kind === "paratroopa" && e.onGround && e.flapTimer !== undefined) {
        e.flapTimer -= dt;
        if (e.flapTimer <= 0) {
          e.vy = 9; // jump up
          e.flapTimer = 2 + Math.random() * 2; // next flap in 2-4 seconds
        }
      }

      if (e.kind === "shell") {
        // keep velocity (kicked or zero), small friction on ground
      } else {
        e.vx = ENEMY_SPEED * e.facing;
        e.walkPhase += dt;
      }

      const body: Body = { x: e.x, y: e.y, w: e.w, h: e.h, vx: e.vx, vy: e.vy };
      const res = moveBody(this.level, body, dt);
      e.x = body.x;
      e.y = body.y;
      e.vx = body.vx;
      e.vy = body.vy;

      if (res.hitWall) {
        if (e.kind === "shell") {
          // shell bounces; also smash bricks in the way
          e.vx = SHELL_KICK_SPEED * -res.wallDir;
          const tx = Math.floor(e.x + (res.wallDir > 0 ? e.w / 2 + 0.2 : -e.w / 2 - 0.2));
          const ty = Math.floor(e.y);
          const t = this.level.tiles.get(tileKey(tx, ty));
          if (t && t.type === 2) this.breakBrick(tileKey(tx, ty));
        } else {
          e.facing = (e.facing * -1) as 1 | -1;
          // turn at cliff edge
          const dir = e.facing;
          if (res.onGround && !hasGroundAhead(this.level, e.x + dir * 0.1, e.y - e.h / 2, dir as 1 | -1)) {
            e.facing = (e.facing * -1) as 1 | -1;
          }
        }
      }

      if (e.y < -6) {
        e.dead = true;
        e.squishTimer = 0;
        toRemove.push(e);
        continue;
      }

      e.rig.group.position.set(e.x, e.y - e.h / 2, 0);
      e.rig.group.rotation.y = 0;
      e.rig.group.scale.x = e.facing;
      e.rig.update(e.walkPhase, e.kind !== "shell" || e.vx !== 0);

      // shell kills other enemies
      if (e.kind === "shell" && Math.abs(e.vx) > 0.1) {
        for (const o of this.enemies) {
          if (o === e || o.dead || o.kind === "shell") continue;
          if (aabb(e.x, e.y, e.w, e.h, o.x, o.y, o.w, o.h)) {
            this.killEnemy(o, true);
          }
        }
      }

      // player collision
      if (p.alive && this.phase === "playing" && p.invulnTimer <= 0) {
        if (aabb(p.x, p.y, p.w, p.h, e.x, e.y, e.w, e.h)) {
          const playerFeet = p.y - p.h / 2;
          const enemyHead = e.y + e.h / 2;
          if (p.vy < -0.5 && playerFeet > enemyHead - 0.5) {
            // stomp
            if (e.kind === "shell") {
              this.kickShell(e, p.facing);
            } else {
              this.killEnemy(e, false);
            }
            p.vy = input.jump ? 13 : 8;
            this.sfx.stomp();
            this.pushHud();
          } else if (e.kind === "shell" && e.vx === 0) {
            // walk into dormant shell: kick it
            this.kickShell(e, p.facing);
          } else {
            this.damagePlayer();
          }
        }
      }
    }

    for (const e of toRemove) {
      this.worldGroup.remove(e.rig.group);
      this.enemies.splice(this.enemies.indexOf(e), 1);
    }
  }

  private updatePowerups(dt: number): void {
    const p = this.player;
    for (const m of this.powerups) {
      if (m.exiting) {
        m.y += 2.2 * dt;
        if (m.y >= m.exitTo) {
          m.y = m.exitTo;
          m.exiting = false;
        }
      } else {
        m.vy = Math.max(MAX_FALL, m.vy + GRAVITY * dt);
      }
      if (!m.exiting) {
        m.vx = MUSHROOM_SPEED * -1;
      } else {
        m.vx = 0;
      }

      const body: Body = { x: m.x, y: m.y, w: m.w, h: m.h, vx: m.vx, vy: m.vy };
      if (!m.exiting) {
        const res = moveBody(this.level, body, dt);
        m.x = body.x;
        m.y = body.y;
        m.vx = body.vx;
        m.vy = body.vy;
        if (res.hitWall) m.vx = MUSHROOM_SPEED;
      }

      m.rig.group.position.set(m.x, m.y - m.h / 2, 0);

      if (p.alive && this.phase === "playing" && aabb(p.x, p.y, p.w, p.h, m.x, m.y, m.w, m.h)) {
        m.dead = true;
        if (p.power === "small") {
          this.setPower("big");
          this.sfx.powerup();
          this.addScore(1000);
        } else {
          this.addScore(1000);
          this.sfx.powerup();
        }
        this.pushHud();
      }
    }
    this.powerups = this.powerups.filter((m) => {
      if (m.dead || m.y < -6) {
        this.worldGroup.remove(m.rig.group);
        return false;
      }
      return true;
    });
  }

  private updateCoinPops(dt: number): void {
    for (const c of this.coinPops) {
      c.life -= dt;
      c.vy += GRAVITY * 0.6 * dt;
      c.y += c.vy * dt;
      c.mesh.position.y = c.y;
      c.mesh.rotation.y += dt * 12;
    }
    this.coinPops = this.coinPops.filter((c) => {
      if (c.life <= 0) {
        this.worldGroup.remove(c.mesh);
        return false;
      }
      return true;
    });
  }

  private updateBumps(dt: number): void {
    for (const b of this.bumps) {
      b.timer -= dt;
      b.mesh.position.y = b.baseY + Math.sin((0.25 - b.timer) * Math.PI * 2) * 0.18;
    }
    this.bumps = this.bumps.filter((b) => {
      if (b.timer <= 0) {
        b.mesh.position.y = b.baseY;
        return false;
      }
      return true;
    });
  }

  private updatePhase(dt: number): void {
    if (this.phase === "dying") {
      this.phaseTimer += dt;
      if (this.phaseTimer > 3) {
        this.stats.lives--;
        if (this.stats.lives <= 0) {
          this.phase = "gameover";
          this.message = "GAME OVER";
          this.phaseTimer = 0;
          this.pushHud();
        } else {
          this.resetPlayer(false);
        }
      }
      return;
    }

    if (this.phase === "gameover") {
      this.phaseTimer += dt;
      if (this.phaseTimer > 3) {
        this.world = 1;
        this.levelNum = 1;
        this.playerPower = "small";
        this.stats.lives = 3;
        this.stats.score = 0;
        this.stats.coins = 0;
        this.resetPlayer(true);
        this.level = buildLevel();
        this.buildWorld();
        this.spawnEnemies();
        this.powerups = [];
        this.coinPops = [];
        this.pipeEnter = { entering: false, entryPx: 0, exitX: 0, exitY: 0, timer: 0 };
        this.stats.time = START_TIME;
        this.timeAcc = 0;
        this.phase = "playing";
        this.message = "";
        this.msgTimer = 0;
        this.phaseTimer = 0;
        this.pushHud();
      }
      return;
    }

    if (this.phase === "won") {
      this.phaseTimer += dt;
      const p = this.player;
      // flag slides down, mario slides, then walks to castle
      if (this.phaseTimer < 1.8) {
        const t = this.phaseTimer / 1.8;
        this.winFlagY = 16 - t * 13;
        this.flagRig?.setFlagY(this.winFlagY);
        p.x = this.level.flagX - 0.55;
        p.y = p.h / 2 + (this.winStartY - p.h / 2) * (1 - t);
        p.vx = 0;
        p.vy = 0;
        p.facing = -1;
      } else {
        p.facing = 1;
        p.x += 3.5 * dt;
        let fy = p.h / 2;
        for (let gy = 0; gy >= -4; gy--) {
          if (isSolidAt(this.level, p.x, gy + 0.4)) {
            fy = gy + 1 + p.h / 2;
            break;
          }
        }
        p.y = fy;
        p.walkPhase += dt * 6;
        this.mario.update(p.walkPhase, true);
        if (!this.message && p.x > this.level.flagX + 2) {
          this.message = "COURSE CLEAR";
          this.pushHud();
        }
        if (p.x > this.level.castleX + 5 && this.phaseTimer > 4) {
          this.message = "";
          // cycle: 1-4 -> 2-4 -> 3-4 -> 4-4 -> 1-1
          if (this.world < 4) {
            this.world++;
            this.levelNum = 4;
          } else {
            this.world = 1;
            this.levelNum = 1;
            this.stats.lives = 3;
            this.stats.score = 0;
            this.stats.coins = 0;
          }
          this.resetLevel();
        }
      }
      this.mario.group.position.set(p.x, p.y - p.h / 2, 0);
      this.mario.group.scale.x = p.facing;
      return;
    }

    // playing: timer
    if (this.msgTimer > 0) {
      this.msgTimer -= dt;
      if (this.msgTimer <= 0) {
        this.message = "";
        this.pushHud();
      }
    }
    this.timeAcc += dt;
    while (this.timeAcc >= 1) {
      this.timeAcc -= 1;
      this.stats.time -= 1;
      if (this.stats.time <= 0) {
        this.startDeath();
        break;
      }
    }
    this.pushHud();
  }

  private updateCamera(dt: number): void {
    const p = this.player;
    const targetX = THREE.MathUtils.clamp(p.x + 5, 5, this.level.width - 5);
    const targetY = THREE.MathUtils.clamp(p.y + 2.5, 3, 12);
    const k = Math.min(1, dt * 6);
    this.camera.position.x += (targetX - this.camera.position.x) * k;
    this.camera.position.y += (targetY - this.camera.position.y) * k;
    this.camera.position.z = 13;
    this.camera.lookAt(this.camera.position.x, this.camera.position.y, 0);
  }

  update(dt: number, input: InputState): void {
    const clamped = Math.min(dt, 1 / 30);
    if (this.phase === "playing" || this.phase === "dying") {
      this.updatePlayer(clamped, input);
    }
    if (this.phase === "playing" || this.phase === "won") {
      this.updateEnemies(clamped, input);
    }
    this.updatePowerups(clamped);
    this.updateCoinPops(clamped);
    this.updateBumps(clamped);
    this.updatePhase(clamped);
    this.updateCamera(clamped);
  }

  get playerX(): number {
    return this.player.x;
  }
}
